import { useCallback, useEffect, useRef, useState } from 'react'
import { PING_URL } from './verifyConnectivity'

const PING_INTERVAL_MS = 2000

export type ConnectivityStatus = {
  isConnected: boolean
  isVerifying: boolean
}

/**
 * Low-level hook for monitoring network connectivity.
 * Listens to browser online/offline events and verifies actual connectivity
 * by pinging the PBS health check endpoint.
 */
export function useConnectivityStatus(): ConnectivityStatus {
  const [isConnected, setIsConnected] = useState(navigator.onLine)
  const [isVerifying, setIsVerifying] = useState(false)
  const pingIntervalRef = useRef<number | null>(null)

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      window.clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }, [])

  const startPingVerification = useCallback(() => {
    clearPingInterval()
    setIsVerifying(true)

    pingIntervalRef.current = window.setInterval(() => {
      fetch(PING_URL, { mode: 'no-cors' })
        .then(() => {
          setIsConnected(true)
          setIsVerifying(false)
          clearPingInterval()
        })
        .catch(() => {
          setIsConnected(false)
        })
    }, PING_INTERVAL_MS)
  }, [clearPingInterval])

  const handleConnectionChange = useCallback(() => {
    const browserOnlineStatus = navigator.onLine

    if (browserOnlineStatus) {
      // Browser reports online - verify with actual ping
      startPingVerification()
    } else {
      // Browser reports offline - trust it and stop pinging
      setIsConnected(false)
      setIsVerifying(false)
      clearPingInterval()
    }
  }, [clearPingInterval, startPingVerification])

  useEffect(() => {
    // Initial check
    handleConnectionChange()

    // Listen to browser events
    window.addEventListener('online', handleConnectionChange)
    window.addEventListener('offline', handleConnectionChange)

    return () => {
      window.removeEventListener('online', handleConnectionChange)
      window.removeEventListener('offline', handleConnectionChange)
      clearPingInterval()
    }
  }, [handleConnectionChange, clearPingInterval])

  return { isConnected, isVerifying }
}
